import { ProfileAccessLevel, type ProfileDto, type ProfilesResponseDto } from "../types/dto";

export const filterProfilesByAccessLevel = (
    profiles: readonly ProfileDto[],
    accessLevel: ProfileAccessLevel,
): ProfileDto[] => profiles.filter((profile) => profile.accessLevel === accessLevel);

export const isSharedEmployeeProfile = (profile: ProfileDto): boolean =>
    profile.accessLevel === ProfileAccessLevel.Account;

/**
 * Similarweb-provided global defaults are only offered to shipping accounts; the user's own private
 * personas and the account-shared ones are always listed.
 */
export const filterProfilesForCompanyAnalysis = (
    profiles: readonly ProfileDto[],
    isShippingAccount: boolean,
): ProfileDto[] =>
    profiles.filter(
        (profile) =>
            profile.accessLevel === ProfileAccessLevel.User ||
            profile.accessLevel === ProfileAccessLevel.Account ||
            (isShippingAccount && profile.accessLevel === ProfileAccessLevel.Global),
    );

/**
 * The Employee profiles library and the contacts filter panel both list account-shared profiles
 * alongside the signed-in user's own private ones. Similarweb-provided global defaults stay out.
 */
export const filterUserAndSharedProfiles = (profiles: readonly ProfileDto[]): ProfileDto[] =>
    profiles.filter(
        (profile) =>
            profile.accessLevel === ProfileAccessLevel.Account || profile.accessLevel === ProfileAccessLevel.User,
    );

/**
 * A shared playbook may only target account-shared personas, since a teammate running it cannot see
 * the author's private ones. A private playbook can target either.
 */
export const filterProfilesForPlaybook = (
    profiles: readonly ProfileDto[],
    isSharedWithAccount: boolean,
): ProfileDto[] =>
    isSharedWithAccount
        ? filterProfilesByAccessLevel(profiles, ProfileAccessLevel.Account)
        : filterUserAndSharedProfiles(profiles);

export const toProfilesResponse = (profiles: ProfileDto[]): ProfilesResponseDto => ({
    profiles,
    totalCount: profiles.length,
});
